// Периодические испытания техпроцесса (модуль quality-records): протоколы
// лаборатории.  Выпуск партии проверяет срок последнего годного.
// Чтение — manufacturing_order.read, запись — quality.manage.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { ErrorBanner } from "@/components/ErrorBanner";
import { useQualityError } from "@/components/OrderNonconformities";
import { PageHeader } from "@/components/PageHeader";
import { ErrorState, LoadingState } from "@/components/States";
import { useAuth } from "@/lib/AuthContext";
import { manufacturingOrdersApi, qualityApi } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

const INPUT_CLS = "w-full px-3 py-2 rounded-md bg-surface border border-border";

export default function PeriodicTests() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const [processKey, setProcessKey] = useState("");

  const processes = useQuery({
    queryKey: ["manufacturing-orders", "processes"],
    queryFn: () => manufacturingOrdersApi.processes(),
    staleTime: 60_000,
  });
  const list = useQuery({
    queryKey: ["quality", "periodic-tests", processKey],
    queryFn: () => qualityApi.periodicTests(processKey || undefined),
  });
  const processName = (key: string) => processes.data?.find((p) => p.key === key)?.name ?? key;

  return (
    <>
      <PageHeader eyebrow={t("quality.eyebrow")} title={t("quality.pt.title")} description={t("quality.pt.description")} />

      <div className="card p-3 mt-3">
        <select className={INPUT_CLS} value={processKey} onChange={(e) => setProcessKey(e.target.value)}>
          <option value="">{t("quality.pt.allProcesses")}</option>
          {(processes.data ?? []).map((p) => (
            <option key={p.key} value={p.key}>
              {p.name}
            </option>
          ))}
        </select>
      </div>

      {hasPermission("quality.manage") && <AddForm processes={processes.data ?? []} defaultKey={processKey} />}

      {list.isLoading && <LoadingState />}
      {list.isError && <ErrorState error={list.error} />}
      {list.data && list.data.length === 0 && (
        <div className="card p-8 mt-4 text-center text-text-muted">{t("quality.pt.empty")}</div>
      )}
      {list.data && list.data.length > 0 && (
        <section className="card mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-alt text-text-muted">
              <tr>
                <th className="px-3 py-2 text-left">{t("quality.pt.testedOn")}</th>
                <th className="px-3 py-2 text-left">{t("quality.pt.process")}</th>
                <th className="px-3 py-2 text-left">{t("quality.pt.protocolNo")}</th>
                <th className="px-3 py-2 text-left">{t("quality.pt.lab")}</th>
                <th className="px-3 py-2 text-left">{t("quality.pt.result")}</th>
                <th className="px-3 py-2 text-left">{t("quality.pt.note")}</th>
                <th className="px-3 py-2 text-left">{t("common.created")}</th>
              </tr>
            </thead>
            <tbody>
              {list.data.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{r.tested_on}</td>
                  <td className="px-3 py-2">{processName(r.process_key)}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.protocol_no}</td>
                  <td className="px-3 py-2">{r.lab ?? "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`pill text-xs ${r.passed ? "pill-ok" : "pill-danger"}`}>
                      {r.passed ? t("quality.pt.passed") : t("quality.pt.failed")}
                    </span>
                  </td>
                  <td className="px-3 py-2">{r.note ?? ""}</td>
                  <td className="px-3 py-2 text-xs text-text-muted whitespace-nowrap">{formatDateTime(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </>
  );
}

function AddForm({ processes, defaultKey }: { processes: Array<{ key: string; name: string }>; defaultKey: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const errText = useQualityError();
  const [processKey, setProcessKey] = useState(defaultKey);
  const [testedOn, setTestedOn] = useState("");
  const [protocolNo, setProtocolNo] = useState("");
  const [lab, setLab] = useState("");
  const [passed, setPassed] = useState(true);
  const [note, setNote] = useState("");
  const key = processKey || (processes.length === 1 ? processes[0]!.key : "");

  const create = useMutation({
    mutationFn: () =>
      qualityApi.createPeriodicTest({
        process_key: key,
        tested_on: testedOn,
        protocol_no: protocolNo.trim(),
        passed,
        ...(lab.trim() ? { lab: lab.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quality", "periodic-tests"] });
      setTestedOn("");
      setProtocolNo("");
      setNote("");
    },
  });

  return (
    <section className="card p-4 mt-4 space-y-3">
      <h2 className="text-base">{t("quality.pt.add")}</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <select className={INPUT_CLS} value={key} onChange={(e) => setProcessKey(e.target.value)}>
          <option value="">{t("quality.pt.process")} —</option>
          {processes.map((p) => (
            <option key={p.key} value={p.key}>
              {p.name}
            </option>
          ))}
        </select>
        <input className={INPUT_CLS} type="date" value={testedOn} onChange={(e) => setTestedOn(e.target.value)} />
        <input
          className={INPUT_CLS}
          placeholder={t("quality.pt.protocolNo")}
          value={protocolNo}
          onChange={(e) => setProtocolNo(e.target.value)}
        />
        <input className={INPUT_CLS} placeholder={t("quality.pt.lab")} value={lab} onChange={(e) => setLab(e.target.value)} />
        <input className={INPUT_CLS} placeholder={t("quality.pt.note")} value={note} onChange={(e) => setNote(e.target.value)} />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={passed} onChange={(e) => setPassed(e.target.checked)} />
          {t("quality.pt.passed")}
        </label>
      </div>
      {create.isError && <ErrorBanner onDismiss={() => create.reset()}>{errText(create.error)}</ErrorBanner>}
      <button
        className="btn btn-sm btn-primary"
        disabled={!key || !testedOn || !protocolNo.trim() || create.isPending}
        onClick={() => create.mutate()}
      >
        {t("common.add")}
      </button>
    </section>
  );
}
